import React, {
  FunctionComponent,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import { AtlassianAppSwitcherPopover } from 'app/gamma/src/components/popovers/atlassian-app-switcher';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import { usePopover } from '../popover';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag, featureFlagClient } from '@trello/feature-flag-client';
import { matches } from '@trello/locale';

enum AtlassianSwitcherVariations {
  CONTROL = 'control',
  NOT_ENROLLED = 'not-enrolled',
  EXPERIMENT = 'experiment',
}

interface AtlassianAppSwitcherButtonProps {
  member: MemberModel | undefined;
}

export const AtlassianAppSwitcherButton: FunctionComponent<AtlassianAppSwitcherButtonProps> = ({
  member,
}) => {
  const locale = member?.prefs?.locale;
  const isAaMastered = member?.isAaMastered;
  const switcherVariation = useFeatureFlag(
    'a4aa.atlassian-app-switcher',
    AtlassianSwitcherVariations.NOT_ENROLLED,
  );

  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const appSwitcherMenuButtonRef = useRef<HTMLButtonElement>(null);
  const { isPopoverShown, showPopover } = usePopover({
    name: HeaderTestIds.AtlassianAppSwitcher,
  });

  const onClick = useCallback(() => {
    showPopover();
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianSwitcherHeaderButton',
      source: 'atlassianSwitcherHeaderButton',
    });
  }, [showPopover]);

  useEffect(() => {
    const checkShowAppSwitcher = async () => {
      // If the user isn't enrolled, do nothing.
      if (switcherVariation === AtlassianSwitcherVariations.NOT_ENROLLED) {
        return;
      }
      // If the user has an Atlassian Account and has an English locale...
      if (isAaMastered && typeof locale === 'string' && matches('en', locale)) {
        // Fire the exposure event.
        featureFlagClient.getTrackedVariation(
          'a4aa.atlassian-app-switcher',
          AtlassianSwitcherVariations.NOT_ENROLLED,
        );
        // Only actually show the switcher to users in the "experiment" variation.
        if (switcherVariation === AtlassianSwitcherVariations.EXPERIMENT) {
          setShowAppSwitcher(true);
        }
      }
    };

    checkShowAppSwitcher();
  }, [switcherVariation, isAaMastered, locale]);

  return showAppSwitcher ? (
    <>
      <HeaderButton
        icon={IconName.AppSwitcher}
        onClick={onClick}
        ref={appSwitcherMenuButtonRef}
        testId={HeaderTestIds.AtlassianAppSwitcher}
      />
      {isPopoverShown && (
        <AtlassianAppSwitcherPopover
          target={() => appSwitcherMenuButtonRef.current}
        />
      )}
    </>
  ) : null;
};
